import {
  DerivedAthleteParams,
  ClimateCondition,
  ClimateImpact,
} from '../types/athleteProfile'
import { WeatherForecastData, HourlyWeather } from './weatherApi'

// Helper to calculate Heat Index (feels like)
function calculateHeatIndex(tempC: number, humidity: number): number {
  if (tempC < 20) return tempC
  // Simplified Heat Index approximation
  return (
    tempC +
    0.5555 *
      (6.11 * Math.exp(5417.753 * (1 / 273.16 - 1 / (273.15 + tempC))) - 10) *
      (humidity / 100)
  )
}

function getHeatCategory(
  temp: number,
  humidity: number,
): ClimateCondition['heatIndex'] {
  // Neutral: 5-18C, humidity < 70%
  // Warm/Humid: >= 18C OR humidity >= 75%
  // Hot/Severe: >= 25C OR humidity >= 85%
  // Cold: <= 5C

  if (temp <= 5) return 'cold'
  if (temp >= 25 || humidity >= 85) return 'hot'
  if (temp >= 18 || humidity >= 75) return 'warm'
  return 'neutral'
}

export function getClimateCondition(
  weather: WeatherForecastData | null,
  segmentTime: Date,
): ClimateCondition {
  // Default neutral conditions if no weather data
  if (!weather) {
    return {
      temperature: 15,
      humidity: 50,
      windSpeed: 0,
      feelsLike: 15,
      heatIndex: 'neutral',
    }
  }

  // Find closest hourly forecast
  const timeMs = segmentTime.getTime()
  let closest: HourlyWeather | null = null
  let minDiff = Infinity

  for (const block of weather.blocks) {
    for (const hour of block.hours) {
      const diff = Math.abs(new Date(hour.time).getTime() - timeMs)
      if (diff < minDiff) {
        minDiff = diff
        closest = hour
      }
    }
  }

  if (!closest) {
    // Fallback to average of first block
    const firstBlock = weather.blocks[0]
    return {
      temperature: firstBlock.avgTemp,
      humidity: firstBlock.avgHumidity,
      windSpeed: firstBlock.maxWind,
      feelsLike: firstBlock.avgTemp,
      heatIndex: getHeatCategory(firstBlock.avgTemp, firstBlock.avgHumidity),
    }
  }

  const feelsLike = calculateHeatIndex(closest.temperature, closest.humidity)

  return {
    temperature: closest.temperature,
    humidity: closest.humidity,
    windSpeed: closest.windSpeed,
    feelsLike,
    heatIndex: getHeatCategory(closest.temperature, closest.humidity),
  }
}

export function calculateClimateImpact(
  condition: ClimateCondition,
  athleteParams: DerivedAthleteParams,
  elapsedMinutes: number,
): ClimateImpact {
  const impact: ClimateImpact = {
    pacingMultiplier: 1.0,
    hydrationFactor: 1.0,
    sodiumFactor: 1.0,
    carbFactor: 1.0,
  }

  // 1. Heat Impact
  // Warm/Humid: +15-20% fluids, +20% sodium, -5% carbs
  // Hot/Severe: +25-35% fluids, +30-40% sodium, -10% carbs

  if (condition.heatIndex === 'hot') {
    // Base penalty
    const heatDelta = Math.max(0, condition.feelsLike - 18)
    const rawPenalty = 1.0 + 0.02 * heatDelta * athleteParams.heatPacingPenalty

    // Scale with duration (minimal early, stronger later)
    const durationFactor = Math.min(1.5, 1.0 + (elapsedMinutes / 300) * 0.3)

    impact.pacingMultiplier *= rawPenalty * durationFactor

    // Nutrition adjustments
    impact.hydrationFactor *= 1.3 * athleteParams.hydrationMultiplier
    impact.sodiumFactor *= 1.35 * athleteParams.sodiumMultiplier
    impact.carbFactor *= 0.9 * athleteParams.carbAbsorptionModifier
  } else if (condition.heatIndex === 'warm') {
    const heatDelta = Math.max(0, condition.feelsLike - 15)
    const rawPenalty = 1.0 + 0.01 * heatDelta * athleteParams.heatPacingPenalty
    const durationFactor = Math.min(1.3, 1.0 + (elapsedMinutes / 300) * 0.2)

    impact.pacingMultiplier *= rawPenalty * durationFactor

    impact.hydrationFactor *= 1.15 * athleteParams.hydrationMultiplier
    impact.sodiumFactor *= 1.2 * athleteParams.sodiumMultiplier
    impact.carbFactor *= 0.95 * athleteParams.carbAbsorptionModifier
  }

  // 2. Cold Impact
  if (condition.heatIndex === 'cold') {
    // Cold start penalty (only first hour)
    if (elapsedMinutes < 60) {
      impact.pacingMultiplier *= 1.0 + athleteParams.coldStartPenalty
    }
    // Reduced fluids, carbs baseline
    impact.hydrationFactor *= 0.9
  }

  // 3. Humidity Impact (independent of heat index category if high)
  if (condition.humidity > 80 && condition.temperature > 15) {
    impact.pacingMultiplier *= 1.02
    impact.hydrationFactor *= 1.1
  }

  return impact
}
